import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { FaLock } from 'react-icons/fa';
import { BsCpu } from 'react-icons/bs';
import { HiOutlineExclamation, HiOutlineBell, HiOutlineRefresh, HiOutlineClock } from 'react-icons/hi';
import DashMedidasButton from '../../../DashMedidas/components/DashMedidasButton/DashMedidasButton';
import { SguUnitsSideMenu, SguUnitMenuItem } from '../../../../components/SguUnitsMenu';
import '../../../DashMedidas/DashMedidas.css';
import '../../../../styles/SguUnitsPage.css';
import './ListaEquipamentos.css';

const ListaEquipamentos = (props) => {
  const {
    user,
    points,
    setSelectedEquipment,
    setHistoricEquipment,
    isHistoricMode,
    equipmentsStatus,
    measurementReceived,
    highlightedSerials,
    messageReceived,
    handlePageMap,
    handleSearchData,
    clearEquipmentSearch,
    searchValue,
    liveRotationStatuses = {},
    lastMeasurementsMap = {},
  } = props;

  const [sortedPoints, setSortedPoints] = useState([]);
  const [activeSerial, setActiveSerial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ visible: false, label: '', x: 0, y: 0 });

  const showTooltip = (e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ visible: true, label, x: rect.right + 8, y: rect.top + rect.height / 2 });
  };
  const hideTooltip = () => setTooltip({ visible: false, label: '', x: 0, y: 0 });

  const units = JSON.parse(localStorage.getItem('units')) || [JSON.parse(localStorage.getItem('cooperative'))] || [];

  const companyMap = useMemo(() => {
    return units.reduce((acc, unit) => {
      if (Array.isArray(unit?.equipments)) {
        unit.equipments.forEach(serial => {
          acc[serial] = unit.name;
        });
      }
      return acc;
    }, {});
  }, [units]);

  // Mapa de SEMT serial → dados do alarme vinculado (minHumidity, maxHumidity, active)
  const alarmMap = useMemo(() => {
    const map = {};
    units.forEach(unit => {
      const otherEquipments = Array.isArray(unit?.otherEquipments) ? unit.otherEquipments : [];
      otherEquipments
        .filter(e => e.serialNumber?.toUpperCase().includes('ALRM'))
        .forEach(alarm => {
          if (Array.isArray(alarm.equipments)) {
            alarm.equipments.forEach(semtSerial => {
              if (semtSerial) {
                map[semtSerial.toUpperCase()] = {
                  minHumidity: alarm.minHumidity,
                  maxHumidity: alarm.maxHumidity,
                  active: alarm.active,
                  alarmSerial: alarm.serialNumber,
                  description: alarm.description,
                };
              }
            });
          }
        });
    });
    return map;
  }, [units]);

  const rotationMap = useMemo(() => {
    const map = {};
    units.forEach(unit => {
      const otherEquipments = Array.isArray(unit?.otherEquipments) ? unit.otherEquipments : [];
      otherEquipments
        .filter(e => e.serialNumber?.toUpperCase().includes('RODZ'))
        .forEach(rotation => {
          if (Array.isArray(rotation.equipments)) {
            rotation.equipments.forEach(semtSerial => {
              if (semtSerial) {
                map[semtSerial.toUpperCase()] = {
                  rotationSerial: rotation.serialNumber,
                  description: rotation.description,
                  rotationOn: String(rotation.rotationStatus).toUpperCase() === 'ON',
                };
              }
            });
          }
        });
    });
    return map;
  }, [units]);

  const getLastMeasurement = (serial) => {
    // Prioridade 1: medida recebida via socket — usa receivedAt para verificar recência
    const live = lastMeasurementsMap[serial];
    if (live?.humidity != null) {
      return {
        humidity: Number(live.humidity),
        date: live.receivedAt ?? live.date ?? new Date().toISOString(),
      };
    }
    // Prioridade 2: última medida conhecida do poll de status
    const status = equipmentsStatus.find(item => item.serialnumber === serial);
    if (status?.measurement?.humidity != null) {
      return { humidity: Number(status.measurement.humidity), date: status.measurement.date };
    }
    return null;
  };




  const isAlarmActive = (serial) => {
    // Verifica apenas o status enviado pelo backend
    const status = equipmentsStatus.find(item => item.serialnumber === serial);
    return status?.alarm === 'ON';
  };

  const orderByLastMeasurementDate = (points, equipmentsStatus) => {
    return points.slice().sort((a, b) => {
      const serialA = a.properties.data.serial;
      const serialB = b.properties.data.serial;

      const statusA = equipmentsStatus.find(item => item.serialnumber === serialA) || {};
      const statusB = equipmentsStatus.find(item => item.serialnumber === serialB) || {};

      if (statusA.status === 'online' && statusB.status !== 'online') return -1;
      if (statusA.status !== 'online' && statusB.status === 'online') return 1;

      const measurementA = statusA.measurement ? new Date(statusA.measurement.date).getTime() : 0;
      const measurementB = statusB.measurement ? new Date(statusB.measurement.date).getTime() : 0;

      return measurementB - measurementA;
    });
  };

  const moveNewMeasurementToTop = (orderedPoints, newMeasurementSerial) => {
    const newMeasurementPoint = orderedPoints.find(
      point => point.properties.data.serial === newMeasurementSerial
    );

    const filteredPoints = orderedPoints.filter(
      point => point.properties.data.serial !== newMeasurementSerial
    );

    if (newMeasurementPoint) {
      filteredPoints.unshift(newMeasurementPoint);
    }

    return filteredPoints;
  };

  useEffect(() => {
    let orderedPoints = orderByLastMeasurementDate(points, equipmentsStatus);
    if (measurementReceived?.serialnumber) {
      orderedPoints = moveNewMeasurementToTop(orderedPoints, measurementReceived.serialnumber);
    }
    if (!isHistoricMode) {
      setSortedPoints(orderedPoints);
    }

    setIsLoading(false);

  }, [measurementReceived, points, equipmentsStatus]);

  const handleViewMapClick = () => {
    if (typeof clearEquipmentSearch === "function") {
      clearEquipmentSearch();
    }
    handlePageMap("map");
    setActiveSerial(null);
  };

  const handleEquipmentClick = (equip) => {
    setSelectedEquipment(equip.properties.data);
    setActiveSerial(equip.properties.data.serial);
  };

  const renderEquipment = (equip) => {
    const serial = equip?.properties?.data?.serial;
    const companyName = companyMap[serial] || "";
    const isHighlighted = highlightedSerials.includes(serial);
    const isActive = activeSerial === serial;
    const daysToActivate = equip?.properties?.data?.daystoactivate;
    const serialUpper = serial?.toUpperCase();
    const linkedAlarm = alarmMap[serialUpper];
    const baseRotation = rotationMap[serialUpper];
    const equipmentStatus = equipmentsStatus.find(item => item.serialnumber === serial);
    const apiRotation = equipmentStatus?.rotation; // 'ON', 'OFF' ou undefined

    const linkedRotation = baseRotation
      ? {
        ...baseRotation,
        rotationOn: baseRotation.rotationSerial in liveRotationStatuses
          ? String(liveRotationStatuses[baseRotation.rotationSerial]).toUpperCase() === 'ON'
          : (apiRotation !== undefined ? apiRotation === 'ON' : baseRotation.rotationOn),
        apiRotationStatus: apiRotation,
        socketRotationStatus: baseRotation.rotationSerial in liveRotationStatuses
          ? (String(liveRotationStatuses[baseRotation.rotationSerial]).toUpperCase() === 'ON' ? 'ON' : 'OFF')
          : null
      }
      : null;
    const alarmIndicator = isAlarmActive(serial);
    const lastMeasure = getLastMeasurement(serial);
    const currentHumidity = lastMeasure?.humidity;

    const isHumidityOutRange = linkedAlarm &&
      linkedAlarm.active &&
      currentHumidity != null &&
      (parseFloat(currentHumidity) < parseFloat(linkedAlarm.minHumidity) ||
        parseFloat(currentHumidity) > parseFloat(linkedAlarm.maxHumidity));

    const isWarningIndicator = !alarmIndicator && isHumidityOutRange;

    const alarmStatusText = equipmentStatus?.alarm === 'ON' ? 'ATIVO' : (equipmentStatus?.alarm === 'OFF' ? 'NORMAL' : 'Sem status');

    return (
      <SguUnitMenuItem
        key={`${equip?.properties?.id}-index`}
        active={isActive || isHighlighted}
        alert={alarmIndicator}
        warning={isWarningIndicator}
        blocked={daysToActivate === 0 || daysToActivate === "0"}
        className={isHighlighted ? "equipment-new-data-flash" : ""}
        title={user?.hideSensitiveInfo ? "*****" : companyName}
        topRight={
          <div className="home-equip-item__actions">
            {linkedAlarm && (
              <HiOutlineBell
                size={18}
                className={
                  alarmIndicator
                    ? "home-equip-item__icon home-equip-item__icon--alert"
                    : "home-equip-item__icon"
                }
                onMouseEnter={(e) =>
                  showTooltip(
                    e,
                    `Status: ${alarmStatusText}${
                      linkedAlarm?.description ? ` - ${linkedAlarm.description}` : ""
                    }`
                  )
                }
                onMouseLeave={hideTooltip}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {linkedRotation && (
              <HiOutlineRefresh
                size={18}
                className={[
                  "home-equip-item__icon",
                  linkedRotation.rotationOn
                    ? "home-equip-item__icon--on icon-spinning"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onMouseEnter={(e) => {
                  let rotationText = `Rodízio ${
                    linkedRotation.rotationOn ? "ativo" : "inativo"
                  }`;
                  if (
                    linkedRotation.socketRotationStatus &&
                    linkedRotation.apiRotationStatus &&
                    linkedRotation.socketRotationStatus !==
                      linkedRotation.apiRotationStatus
                  ) {
                    rotationText += ` (Socket: ${linkedRotation.socketRotationStatus}, API: ${linkedRotation.apiRotationStatus})`;
                  }
                  showTooltip(
                    e,
                    `${rotationText}${
                      linkedRotation.description
                        ? ` - ${linkedRotation.description}`
                        : ""
                    }`
                  );
                }}
                onMouseLeave={hideTooltip}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <HiOutlineClock
              size={18}
              className="home-equip-item__icon home-equip-item__icon--action"
              onClick={(e) => {
                e.stopPropagation();
                handlePageMap("map");
                setHistoricEquipment(serial);
                setActiveSerial(serial);
              }}
              onMouseEnter={(e) => showTooltip(e, "Ver histórico")}
              onMouseLeave={hideTooltip}
            />
          </div>
        }
        onClick={() => handleEquipmentClick(equip)}
      >
        {equip?.properties?.data?.nameEquipament ? (
          <p className="sgu-unit-item__meta home-equip-item__equip-name">
            {textoLimitado(equip.properties.data.nameEquipament)}
          </p>
        ) : null}
        <p className="sgu-unit-item__meta home-equip-item__serial">{serial}</p>
        {alarmIndicator && (
          <span className="home-equip-item__flag home-equip-item__flag--alert">
            <HiOutlineExclamation size={14} />
            Alarme Ativo
          </span>
        )}
        {isWarningIndicator && (
          <span className="home-equip-item__flag home-equip-item__flag--warning">
            <HiOutlineExclamation size={14} />
            Alerta de Medida
          </span>
        )}
        {messageReceived[serial] && (
          <p className="sgu-unit-item__meta">{messageReceived[serial]}</p>
        )}
        {getEquipmentStatus(equip)}
      </SguUnitMenuItem>
    );
  };

  const textoLimitado = (texto) => {
    return texto.length <= 25 ? texto : texto.slice(0, 25) + '...';
  };

  const getEquipmentStatus = (equip) => {
    const index = equipmentsStatus.findIndex(item => item?.serialnumber === equip?.properties?.data?.serial);

    if (index === -1) {
      return getStatusComponentWithTime('sem status');
    }

    const equipmentStatus = equipmentsStatus[index];

    const status = equipmentStatus.status;
    const updatedAt = equipmentStatus.updatedAt;
    const unlockData = equipmentStatus?.unlockData?.updatedAt;
    const strong = equipmentStatus?.strong;

    return getStatusComponentWithTime(status, updatedAt, unlockData, equip, strong);
  };

  const getStatusComponentWithTime = (status, updatedAt = null, unlockData = null, equip = null, strong = false) => {
    const now = new Date();
    let statusText = "Sem status";
    let statusClass = "secondary"; // Classe Bootstrap padrão para cinza

    const formatDuration = (date, patternText) => {
      const diff = Math.abs(now - new Date(date));
      const duration = moment.duration(diff);

      if (duration.asMinutes() < 60) {
        return `${patternText} ${Math.floor(duration.asMinutes())} ${duration.asMinutes() > 1 ? "minutos" : "minuto"}`;
      } else if (duration.asHours() < 24) {
        return `${patternText} ${Math.floor(duration.asHours())} ${duration.asHours() > 1 ? "horas" : "hora"}`;
      } else {
        return `${patternText} ${Math.floor(duration.asDays())} ${duration.asDays() > 1 ? "dias" : "dia"}`;
      }
    };

    if (updatedAt) {
      if (status === "online") {
        const serial = equip?.properties?.data?.serial || "";
        if (serial.includes("SIMT") || serial.includes("SEMT")) {
          statusText = strong ? "Conectado (Sinal bom)" : "Conectado";
        } else {
          statusText = "Conectado";
        }
        statusClass = "success";
      } else {
        statusText = formatDuration(updatedAt, "Última conexão há");
        statusClass = "danger"; // Verde para online, vermelho para offline
      }
    }

    let lastActivation = unlockData ? formatDuration(unlockData, "Última ativação há") : "";
    const daysToActivate = equip?.properties?.data?.daystoactivate;

    return (
      <div className="home-equip-item__status">
        <div className="home-equip-item__status-row">
          {statusClass === "success" && (
            <span className="home-equip-item__status-dot" aria-hidden>
              🟢
            </span>
          )}
          <span
            className={[
              "home-equip-item__status-text",
              statusClass === "success" ? "is-online" : "is-offline",
            ].join(" ")}
          >
            {statusText}
          </span>
        </div>

        <div className={statusClass === "success" ? "home-equip-item__status-extra" : ""}>
          {lastActivation && (
            <p className="sgu-unit-item__meta mb-0">{lastActivation}</p>
          )}

          {daysToActivate === "0" && (
            <p className="home-equip-item__blocked-label mb-0">
              <FaLock size={12} />
              Bloqueado
            </p>
          )}
        </div>
      </div>
    );
  };

  const headerRight = (
    <span className="dash-section-card__header-meta">
      {sortedPoints?.length || 0} item{(sortedPoints?.length || 0) !== 1 ? "s" : ""}
    </span>
  );

  const listItems = isHistoricMode
    ? (sortedPoints || []).filter(
        (equip) => equip?.properties?.data?.serial === activeSerial
      )
    : sortedPoints || [];

  return (
    <>
      <SguUnitsSideMenu
        icon={BsCpu}
        subtitle="Seleção"
        title="Equipamentos"
        headerRight={headerRight}
        sectionId="home-equipamentos"
        showSearch={!isHistoricMode}
        searchValue={searchValue}
        onSearch={handleSearchData}
        loading={isLoading}
        bodyClassName="home-equip-card-body"
        className="home-equip-side-menu"
        scrollClassName="home-equip-list__scroll"
        toolbarExtra={
          <DashMedidasButton
            className="w-100"
            variant={isHistoricMode ? "warning" : "success"}
            onClick={() => {
              if (isHistoricMode) {
                setHistoricEquipment("");
              } else {
                handleViewMapClick();
              }
            }}
          >
            {isHistoricMode ? "Voltar" : "Ver mapa de equipamentos"}
          </DashMedidasButton>
        }
        emptyText="Nenhum equipamento encontrado"
      >
        {listItems.map(renderEquipment)}
      </SguUnitsSideMenu>

      {tooltip.visible && (
        <div
          className="lista-equip-tooltip"
          style={{ top: tooltip.y, left: tooltip.x }}
        >
          {tooltip.label}
        </div>
      )}
    </>
  );
};

export default ListaEquipamentos;
